/**
 * Fundraising summary for the admin dashboard.
 *
 * Deliberately a SEPARATE, clearly-labeled band from the tuition/billing
 * metrics: campaign gifts are their own ledger and never touch revenue,
 * collected, or outstanding figures. Uses a distinct teal accent (not the
 * green/amber of the financial tiles) so it can't be misread as fee revenue.
 *
 * Self-contained: fetches its own roll-up and renders nothing at all unless a
 * campaign is actively running, so the dashboard stays clean until fundraising
 * is live (and disappears again once every campaign has ended). Mount only for
 * managers (the endpoint is manager-only).
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { formatCurrency } from "@/utils/formatters";
import { useCampaignSummary } from "@/hooks/useCampaigns";
import type { CampaignSummaryItem } from "@/types/campaign";

// Teal — intentionally distinct from the financial section's green/amber so
// fundraising never reads as tuition revenue.
const ACCENT = "#0f766e";

const CampaignProgressRow: React.FC<{ item: CampaignSummaryItem }> = ({
  item,
}) => {
  const raised = Number(item.raised_amount);
  const goal = item.goal_amount ? Number(item.goal_amount) : null;
  const pct =
    goal && goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : null;

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "baseline", gap: 1, mb: 0.5 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {item.title}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: ACCENT, whiteSpace: "nowrap" }}>
          {formatCurrency(raised)}
        </Typography>
      </Stack>
      {pct !== null && (
        <Box
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: alpha(ACCENT, 0.14),
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${pct}%`,
              borderRadius: 3,
              bgcolor: ACCENT,
              transition: "width 0.8s cubic-bezier(0.2, 0.7, 0.2, 1)",
            }}
          />
        </Box>
      )}
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {goal ? `${pct}% of ${formatCurrency(goal)} · ` : ""}
        {item.supporter_count} sponsor{item.supporter_count === 1 ? "" : "s"}
      </Typography>
    </Box>
  );
};

interface FundraisingCardProps {
  /** Gate the query by role; when false nothing is fetched or rendered. */
  enabled?: boolean;
}

export const FundraisingCard: React.FC<FundraisingCardProps> = ({
  enabled = true,
}) => {
  const navigate = useNavigate();
  const { data, isLoading } = useCampaignSummary(enabled);

  // Adaptive: show the band only while a campaign is actively running. Ended
  // or draft-only tenants see nothing — the all-time roll-up isn't dashboard
  // signal once nothing is live. Gate on the rendered list itself (not the
  // separate active_count scalar) so a payload that omits `active` — or a
  // count/list mismatch — hides the band instead of crashing on `.map`.
  const activeCampaigns = data?.active ?? [];
  if (!enabled || isLoading || !data || activeCampaigns.length === 0)
    return null;

  return (
    <Box data-section="fundraising" sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Fundraising
      </Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Separate from tuition — campaign gifts don’t affect your revenue or
        collection figures.
      </Typography>

      <Card sx={{ mt: 1.5, boxShadow: 1, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={3} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(ACCENT, 0.12),
                    color: ACCENT,
                    flexShrink: 0,
                  }}
                >
                  <VolunteerActivismRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Raised all-time
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, color: ACCENT, lineHeight: 1.1 }}
                  >
                    {formatCurrency(data.total_raised)}
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 1.5 }}>
                Across {data.campaign_count ?? activeCampaigns.length} campaign
                {(data.campaign_count ?? activeCampaigns.length) === 1 ? "" : "s"}{" "}
                · {data.active_count ?? activeCampaigns.length} active
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              {/* activeCampaigns is non-empty here (guard above). */}
              <Stack spacing={1.75}>
                {activeCampaigns.map((c) => (
                  <CampaignProgressRow key={c.id} item={c} />
                ))}
              </Stack>
            </Grid>
          </Grid>

          <Box sx={{ textAlign: "right", mt: 1.5 }}>
            <Button
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={() => navigate("/campaigns")}
              sx={{ textTransform: "none", fontWeight: 600, color: ACCENT }}
            >
              Manage campaigns
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FundraisingCard;
